import { Badge, Box, Container, Heading, Stack, Text } from '@chakra-ui/react';
import { useAgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/main-operations';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useSharedPageContext } from '../../contexts/SharedPageContext';
import { SharedPage } from '../SharedPage/SharedPage';

/**
 * Scaffold for the public, no-login agent compliance review page. Phase 1
 * renders the transaction name and open-issue count once the share-link token
 * has been verified; the dispute/resubmit UI lands in later phases.
 */
const AgentComplianceReviewContent = () => {
  const { ingestionFolderUuid } = useParams();
  const [searchParams] = useSearchParams();
  // The share-link token is carried as a `token` query param so that future
  // per-document routes can nest under the folder path without threading it.
  const token = searchParams.get('token') ?? undefined;
  const navigate = useNavigate();
  const { isShareLinkVerified } = useSharedPageContext();

  const { data, loading } = useAgentComplianceReviewByShareLinkTokenQuery({
    variables: {
      input: {
        token: token ?? '',
        ingestionFolderUuid: ingestionFolderUuid ?? '',
      },
    },
    // Defer until SharedPage confirms the link is valid and not expired, so
    // expired/invalid links are handled by SharedPage's expired-link card
    // rather than being redirected to /404 by this query.
    skip: !isShareLinkVerified || !token || !ingestionFolderUuid,
    onError: ({ graphQLErrors }) => {
      // The link is already verified valid and unexpired, so a server-side
      // error means the token isn't bound to this folder — treat as not found.
      // Transient network errors are not treated as not-found.
      if (graphQLErrors.length > 0) {
        navigate('/404');
      }
    },
  });

  if (!isShareLinkVerified || loading) {
    return <CenterSpinner />;
  }

  const summary = data?.agentComplianceReviewByShareLinkToken;
  if (!summary) {
    return null;
  }

  return (
    <Container maxWidth="container.md" py={8}>
      <Stack spacing={6}>
        <Box>
          <Text color="gray.500" fontSize="sm" fontWeight="medium">
            Compliance review
          </Text>
          <Heading size="lg">{summary.folderName}</Heading>
        </Box>

        <Box borderColor="gray.200" borderRadius="md" borderWidth="1px" p={4}>
          <Stack align="center" direction="row" spacing={3}>
            <Badge
              colorScheme={summary.openIssueCount > 0 ? 'red' : 'green'}
              fontSize="md"
            >
              {summary.openIssueCount}
            </Badge>
            <Text>
              {summary.openIssueCount === 1 ? 'open issue' : 'open issues'} to
              review
            </Text>
          </Stack>
        </Box>

        {/* Issue list and dispute/resubmit actions are added in later phases. */}
      </Stack>
    </Container>
  );
};

export const AgentComplianceReviewPage = () => {
  const { ingestionFolderUuid } = useParams();

  // Pass the folder to SharedPage so verification is bound to it (and a token
  // for a different folder fails fast at the SharedPage layer).
  return (
    <SharedPage ingestionFolderUuid={ingestionFolderUuid}>
      <AgentComplianceReviewContent />
    </SharedPage>
  );
};
